import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Images, ZoomIn, Eye } from 'lucide-react';

export default function GalleryModal({ isOpen, onClose, albums = [], initialAlbumIndex = 0, initialImageIndex = 0 }) {
  // Flatten all images into a structured list with album metadata
  const allImages = React.useMemo(() => {
    const list = [];
    albums.forEach((album, aIdx) => {
      const albumImages = Array.isArray(album.image || album.billede) 
        ? (album.image || album.billede) 
        : [album.image || album.billede].filter(Boolean);
      
      albumImages.forEach((imgUrl, iIdx) => {
        list.push({
          url: imgUrl,
          albumTitle: album.title || album.titel || 'Området',
          albumDescription: album.description || album.beskrivelse || '',
          albumIndex: aIdx,
          imageIndexInAlbum: iIdx,
          totalInAlbum: albumImages.length
        });
      });
    });
    return list;
  }, [albums]);

  const [selectedAlbumFilter, setSelectedAlbumFilter] = useState('all'); // 'all' or albumIndex number
  const [currentIndex, setCurrentIndex] = useState(0);

  // When opened, set initial active image index
  useEffect(() => {
    if (isOpen) {
      if (initialAlbumIndex !== null && initialAlbumIndex >= 0) {
        // Find first image of that album or specific image
        const targetIndex = allImages.findIndex(
          img => img.albumIndex === initialAlbumIndex && img.imageIndexInAlbum === (initialImageIndex || 0)
        );
        if (targetIndex >= 0) {
          setCurrentIndex(targetIndex);
          setSelectedAlbumFilter(initialAlbumIndex);
        } else {
          setCurrentIndex(0);
          setSelectedAlbumFilter('all');
        }
      } else {
        setCurrentIndex(0);
        setSelectedAlbumFilter('all');
      }
    }
  }, [isOpen, initialAlbumIndex, initialImageIndex, allImages]);

  // Filtered list based on selected tab
  const displayImages = React.useMemo(() => {
    if (selectedAlbumFilter === 'all') return allImages;
    return allImages.filter(img => img.albumIndex === selectedAlbumFilter);
  }, [allImages, selectedAlbumFilter]);

  // Active image in filtered list
  const activeImage = displayImages[currentIndex] || displayImages[0] || allImages[0];

  const handleNext = () => {
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % displayImages.length);
  };

  const handlePrev = () => {
    if (displayImages.length <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, displayImages.length]);

  if (!isOpen || allImages.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-forest-950/95 backdrop-blur-md text-white animate-fade-in">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-4 sm:px-6 py-4 border-b border-forest-800 bg-forest-950/80">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-forest-800/80 flex items-center justify-center text-forest-300">
            <Images className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white font-display leading-tight">
              Billedgalleri — Kongeengen
            </h3>
            <p className="text-xs text-forest-300">
              {displayImages.length > 0 ? (
                <>Billede {currentIndex + 1} af {displayImages.length}</>
              ) : 'Ingen billeder'}
            </p>
          </div>
        </div>

        {/* Album Selector Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto py-1 max-w-full no-scrollbar">
          <button
            onClick={() => {
              setSelectedAlbumFilter('all');
              setCurrentIndex(0);
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
              selectedAlbumFilter === 'all'
                ? 'bg-forest-600 text-white shadow-sm'
                : 'bg-forest-900/60 text-forest-300 hover:bg-forest-800 hover:text-white'
            }`}
          >
            Alle billeder ({allImages.length})
          </button>
          {albums.map((album, idx) => {
            const count = Array.isArray(album.image || album.billede)
              ? (album.image || album.billede).length
              : (album.image || album.billede ? 1 : 0);
            return (
              <button
                key={idx}
                onClick={() => {
                  setSelectedAlbumFilter(idx);
                  setCurrentIndex(0);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  selectedAlbumFilter === idx
                    ? 'bg-forest-600 text-white shadow-sm'
                    : 'bg-forest-900/60 text-forest-300 hover:bg-forest-800 hover:text-white'
                }`}
              >
                {album.title || album.titel || `Album ${idx + 1}`} ({count})
              </button>
            );
          })}
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-forest-900/80 hover:bg-forest-800 text-forest-200 hover:text-white transition-colors cursor-pointer"
          title="Luk galleri (Esc)"
          aria-label="Luk galleri"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main Image Stage */}
      <div className="relative flex-1 flex items-center justify-center p-4 sm:p-8 min-h-0 overflow-hidden">
        {/* Navigation Buttons */}
        {displayImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 sm:left-6 z-10 p-3 rounded-full bg-forest-900/80 hover:bg-forest-700 text-white border border-forest-700/50 shadow-lg transition-all hover:scale-105 cursor-pointer backdrop-blur-sm"
              title="Forrige billede (Venstre piltast)"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 sm:right-6 z-10 p-3 rounded-full bg-forest-900/80 hover:bg-forest-700 text-white border border-forest-700/50 shadow-lg transition-all hover:scale-105 cursor-pointer backdrop-blur-sm"
              title="Næste billede (Højre piltast)"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Image view */}
        {activeImage ? (
          <div className="flex flex-col items-center justify-center max-h-full max-w-5xl w-full">
            <div className="relative max-h-[62vh] sm:max-h-[68vh] w-full flex items-center justify-center">
              <img
                key={activeImage.url}
                src={activeImage.url}
                alt={activeImage.albumTitle}
                className="max-h-[62vh] sm:max-h-[68vh] w-auto max-w-full object-contain rounded-xl shadow-2xl transition-all duration-300"
              />
            </div>

            {/* Caption */}
            <div className="mt-3 text-center max-w-2xl px-4">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-forest-400 bg-forest-900/80 px-2.5 py-0.5 rounded-full mb-1">
                {activeImage.albumTitle}
              </span>
              {activeImage.albumDescription && (
                <p className="text-xs sm:text-sm text-forest-200 line-clamp-2">
                  {activeImage.albumDescription}
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Bottom Thumbnail Strip */}
      {displayImages.length > 1 && (
        <div className="px-4 sm:px-6 py-3 border-t border-forest-800/80 bg-forest-950/90">
          <div className="flex items-center justify-center gap-2 overflow-x-auto py-1 max-w-5xl mx-auto no-scrollbar">
            {displayImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`relative shrink-0 w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden transition-all duration-200 cursor-pointer ${
                  idx === currentIndex
                    ? 'ring-2 ring-forest-400 ring-offset-2 ring-offset-forest-950 scale-105 opacity-100'
                    : 'opacity-50 hover:opacity-90 hover:scale-100'
                }`}
              >
                <img
                  src={img.url}
                  alt={`Miniature ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
